/**
 * Stale-build self-heal (UX growth list N1).
 *
 * After a deploy, tabs that are already open still reference the previous
 * build's hashed chunk files, which no longer exist. Lazy routes then fail
 * with "Failed to fetch dynamically imported module" (or ChunkLoadError).
 * The fix is one hard reload so the new index.html is fetched.
 *
 * The guard matters: if the reload doesn't help (CDN still serving the old
 * index, offline, a genuinely broken chunk), an unguarded reload-on-error
 * loops forever. We allow one automatic reload per RELOAD_WINDOW_MS per tab
 * (sessionStorage), then fall back to showing the error UI.
 */

const GUARD_KEY = 'bcp_chunk_reload_at';
export const RELOAD_WINDOW_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: unknown; message?: unknown };
  const name = typeof e.name === 'string' ? e.name : '';
  const message = typeof e.message === 'string' ? e.message : '';
  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS')
  );
}

function readGuard(): number {
  try {
    const raw = sessionStorage.getItem(GUARD_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** True when an automatic reload is allowed right now (none in the last window). */
export function canAutoReload(now: number = Date.now()): boolean {
  const last = readGuard();
  return !last || now - last > RELOAD_WINDOW_MS || now < last;
}

/**
 * Reload once to pick up the new build. Returns false (and does nothing) if we
 * already reloaded for this reason recently, so callers can show an error UI
 * instead of looping.
 */
export function reloadForStaleBuild(now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false;
  if (!canAutoReload(now)) return false;
  try {
    sessionStorage.setItem(GUARD_KEY, String(now));
  } catch {
    // Without sessionStorage we can't guard against a loop, so don't auto-reload.
    return false;
  }
  const reload = () => window.location.reload();
  if ('caches' in window) {
    caches
      .keys()
      .then((names) => Promise.all(names.map((n) => caches.delete(n))))
      .catch(() => undefined)
      .then(reload);
  } else {
    reload();
  }
  return true;
}

let installed = false;

/**
 * Global listeners: Vite's `vite:preloadError` (fired when a modulepreload or
 * dynamic import fails) and unhandled promise rejections from lazy imports
 * that never reach an ErrorBoundary.
 */
export function installSelfHeal(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('vite:preloadError', (event: Event) => {
    if (reloadForStaleBuild()) {
      // Stop Vite from rethrowing; the page is about to reload.
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason) && reloadForStaleBuild()) {
      event.preventDefault();
    }
  });
}

/** Test helper. */
export function __resetSelfHealForTests(): void {
  installed = false;
  try {
    sessionStorage.removeItem(GUARD_KEY);
  } catch {
    /* ignore */
  }
}
